import React, {useState} from 'react';
import styled from 'styled-components';
import Badge from '@material-ui/core/Badge/Badge';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DeviceIcon from '../../components/DeviceIcon';
import DeviceType, {DeviceTypeString} from 'features/POSRegisters/interfaces/DeviceType';
import Register, {RegisterKeys} from 'features/POSRegisters/interfaces/Register';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import ScanDetails from 'features/POSRegisters/interfaces/ScanDetails';
import Device from 'features/POSRegisters/interfaces/Device';
import DeviceModal from 'features/POSRegisters/components/DeviceModal';
import {hasAssociation, hasDeviceInformation} from 'features/POSRegisters/functions/alertTypes';

//#region Styled Components
const StyledDeviceContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin: 0 8px;
`;

const StyledDeviceButton = styled(IconButton)`
  padding: 0;
  font-size: unset;
  color: ${props => props.theme.text};

  &:hover {
    opacity: 0.6;
  }

  &.disabled:hover {
    opacity: 1;
  }

  &.disabled {
    cursor: default;
    color: ${props => props.theme.white}1F;
  }
`;

const StyledToolTip = styled(Tooltip)``;

//#endregion Styled Components

//#region Component
interface DeviceNodesProps {
  register: Register;
}

export default function DeviceNodes({register}: DeviceNodesProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceType>(DeviceType.IPAD);

  const connectedDevices: {
    deviceType: DeviceType;
    key: RegisterKeys;
    relatedDeviceKeys: RegisterKeys[];
  }[] = [
    {deviceType: DeviceType.IPAD, key: 'ipad', relatedDeviceKeys: []},
    {deviceType: DeviceType.IPAD_MINI, key: 'ipadMini', relatedDeviceKeys: []},
    {
      deviceType: DeviceType.RECEIPT_PRINTER,
      key: 'receiptPrinter',
      relatedDeviceKeys: ['barcodeScanner', 'cashDrawer'],
    },
    {deviceType: DeviceType.PAYMENT_TERMINAL, key: 'paymentTerminal', relatedDeviceKeys: []},
  ];

  const handleOpen = (deviceType: DeviceType, isValid: boolean) => {
    if (!isValid) return;
    setSelectedDevice(deviceType);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <StyledDeviceContainer>
      {connectedDevices.map((device, index) => {
        const registerKey = device.key;
        const deviceDetails = register[registerKey] as Device<ScanDetails>;

        const isValid = hasDeviceInformation([deviceDetails]);

        const relatedDevices = device.relatedDeviceKeys.map(key => register[key]);
        const filteredDevices = [deviceDetails, ...relatedDevices].filter(device =>
          hasAssociation(device?.scanResults)
        );

        return (
          <StyledToolTip
            title={DeviceTypeString[device.deviceType]}
            placement="top"
            key={`${index}-${DeviceTypeString[device.deviceType]}`}
            arrow
          >
            <StyledDeviceButton
              onClick={() => handleOpen(device.deviceType, isValid)}
              className={(isValid ? '' : 'disabled') + ' ' + device.deviceType}
            >
              <Badge
                badgeContent={
                  <SeverityBadge severity={getPriorityAlert(filteredDevices)} size={0.65} />
                }
                style={{pointerEvents: 'none'}}
                invisible={false}
                color={undefined}
              >
                <DeviceIcon deviceType={device.deviceType} />
              </Badge>
            </StyledDeviceButton>
          </StyledToolTip>
        );
      })}
      <DeviceModal
        selectedDevice={selectedDevice}
        register={register}
        modalState={open}
        handleClose={handleClose}
      />
    </StyledDeviceContainer>
  );
}
//#endregion Component
